import { atom, useAtom } from 'jotai';
import { useEffect, useRef } from 'react';
import * as Sentry from '@sentry/react';
import { SocketStatus } from '../../../data-models/socket-base.data-model';
import { HudsonWsService } from '../../../services/HudsonWsService';
import { useAgentHealthStatus } from './useAgentHealthStatus';
import { useChatWsStatus } from './useWsChat';

const AutoReconnectCoolDownMs = 30_000;

/** A reconnect after a socket error is in flight. One atom, whichever surface runs the reconnect. */
export const isAutoReconnectingAtom = atom(false);

/**
 * Reconnects a socket that has given up (`SocketStatus.error`) once the agent reports healthy
 * again. Whoever owns the socket's lifecycle runs it (`enabled`): the panel, or the app for a
 * socket it holds open past the panel; the other only reads the state.
 */
export function useAutoReconnect(enabled = true) {
  const serverHealth = useAgentHealthStatus();
  const wsStatus = useChatWsStatus();
  const [isAutoReconnecting, setIsAutoReconnecting] = useAtom(isAutoReconnectingAtom);
  const lastAttemptRef = useRef<number>(0);

  useEffect(() => {
    const shouldReconnect =
      enabled &&
      serverHealth === 'ok' &&
      wsStatus === SocketStatus.error &&
      Date.now() - lastAttemptRef.current > AutoReconnectCoolDownMs;

    if (shouldReconnect && !isAutoReconnecting) {
      setIsAutoReconnecting(true);
      lastAttemptRef.current = Date.now();

      const service = HudsonWsService.get();
      if (service) {
        service.reconnect().finally(() => {
          setIsAutoReconnecting(false);
        });
      } else {
        Sentry.logger.error('[useAutoReconnect] HudsonWsService not initialized');
        setIsAutoReconnecting(false);
      }
    }
  }, [enabled, serverHealth, wsStatus, isAutoReconnecting, setIsAutoReconnecting]);

  return { isAutoReconnecting };
}
